import {
  Button,
  Pill,
  RecordCard,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import type { OutboxMessageResponse } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';
import { derivedStatus, MAX_ATTEMPTS, OutboxRow, statusVariants } from './OutboxRow';
import { outboxStatusLabel } from './systemLabels';

interface Props {
  messages: OutboxMessageResponse[];
  retryingId: string | null;
  onRetry: (id: string) => void;
}

/**
 * Desktop die Datentabelle, darunter gestapelte Karten.
 *
 * <p>Die Spalte „Art" ist gefallen: der Vertrag kennt genau einen Nachrichtentyp
 * (`OutboxMessageTypes.SendEmail`), sie stand also in jeder Zeile gleich. Ausgelöst und
 * versendet teilen sich eine Spalte — beides gleichzeitig gibt es nie.</p>
 */
export function OutboxTable({ messages, retryingId, onRetry }: Props) {
  return (
    // Container-Query statt Viewport-Breakpoint: die Fläche liegt im Einstellungs-Layout
    // und ist auch auf 1440 px nur rund 714 px breit. Ein `md:`-Umschalter hätte die Tabelle
    // dort gezeigt, wo sie nachweislich nicht hineinpasst.
    <div className="@container">
      <div className="@4xl:block hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Zustand</TableHead>
              <TableHead>Zeitpunkt</TableHead>
              <TableHead className="text-right">Versuche</TableHead>
              <TableHead>Letzter Fehler</TableHead>
              <TableHead>
                <span className="sr-only">Aktion</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.map((message) => (
              <OutboxRow
                key={message.id}
                message={message}
                retrying={retryingId === message.id}
                onRetry={() => onRetry(message.id)}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="@4xl:hidden grid gap-3">
        {messages.map((message) => {
          const status = derivedStatus(message);
          return (
            <RecordCard
              key={message.id}
              status={
                <Pill variant={statusVariants[status]} size="sm">
                  {outboxStatusLabel(status)}
                </Pill>
              }
              identityMono
              identity={formatDateTime(message.occurredAt)}
              facts={[
                { label: 'Versuche', value: `${message.attempts} / ${MAX_ATTEMPTS}` },
                {
                  label: 'Versendet',
                  value: message.processedAt ? formatDateTime(message.processedAt) : 'noch nicht',
                },
              ]}
              note={message.lastError}
              action={
                status === 'Failed' ? (
                  <Button
                    variant="ghost"
                    className="min-h-11 w-full"
                    loading={retryingId === message.id}
                    onClick={() => onRetry(message.id)}
                  >
                    Erneut senden
                  </Button>
                ) : null
              }
            />
          );
        })}
      </ul>
    </div>
  );
}
